import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { doc, onSnapshot } from 'firebase/firestore';
import { FiSmartphone, FiCheckCircle, FiAlertCircle, FiLink2, FiLogOut, FiRefreshCw, FiInfo } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../config/firebase';
import PageLayout from '../../components/PageLayout';
import usePagePermissions from '../../hooks/usePagePermissions';
import { callWhatsAppService } from '../../utils/whatsappHelper';

const WhatsAppConnect = () => {
  const { activeCompany } = useAuth();
  const { canEdit } = usePagePermissions('whatsapp', 'connection');

  const [status, setStatus] = useState({ connected: false, connecting: false, qrCode: null });
  const [busy, setBusy] = useState(false);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (!activeCompany?.id) return undefined;
    const ref = doc(db, 'companies', activeCompany.id, 'settings', 'whatsapp');
    const unsub = onSnapshot(ref, (snap) => {
      setStatus(snap.exists() ? snap.data() : { connected: false, connecting: false, qrCode: null });
    }, (err) => {
      console.error('whatsapp status snapshot:', err);
    });
    return unsub;
  }, [activeCompany?.id]);

  const handleConnect = useCallback(async () => {
    if (!canEdit) { setAlert({ type: 'error', message: 'You do not have permission.' }); return; }
    if (!activeCompany?.id) return;
    setBusy(true);
    setAlert({ type: 'info', message: 'Spinning up WhatsApp session… QR should appear in 10–30 seconds. Keep this page open.' });
    try {
      const result = await callWhatsAppService('connect', 'POST', { companyId: activeCompany.id });
      if (result?.ok) {
        setAlert({ type: 'success', message: `Connected as ${result.phone || 'WhatsApp'}.` });
      } else {
        setAlert({ type: 'error', message: result?.error || 'Failed to connect.' });
      }
    } catch (e) {
      console.error(e);
      setAlert({ type: 'error', message: e.message || 'Connection error.' });
    } finally {
      setBusy(false);
    }
  }, [activeCompany?.id, canEdit]);

  const handleDisconnect = useCallback(async () => {
    if (!canEdit) { setAlert({ type: 'error', message: 'You do not have permission.' }); return; }
    if (!window.confirm('Disconnect WhatsApp? You will need to scan the QR again to reconnect.')) return;
    if (!activeCompany?.id) return;
    setBusy(true);
    try {
      await callWhatsAppService('disconnect', 'POST', { companyId: activeCompany.id });
      setAlert({ type: 'success', message: 'WhatsApp disconnected.' });
    } catch (e) {
      console.error(e);
      setAlert({ type: 'error', message: e.message || 'Disconnect error.' });
    } finally {
      setBusy(false);
    }
  }, [activeCompany?.id, canEdit]);

  const isConnected = !!status?.connected;
  const isConnecting = !!status?.connecting;
  const qr = status?.qrCode;

  return (
    <PageLayout pageTitle="WhatsApp Connection">
      <Container>
        <Header>
          <Title><FiSmartphone /> WhatsApp Connection</Title>
          <Description>
            Link your company's WhatsApp number to AMAL ERP. Scan the QR code below with your phone's
            WhatsApp → <strong>Settings → Linked Devices → Link a Device</strong>. Once connected, you can
            send invoices and other documents directly from the ERP.
          </Description>
        </Header>

        {alert && (
          <Alert $type={alert.type}>
            <FiInfo /><span>{alert.message}</span>
          </Alert>
        )}

        <StatusCard $connected={isConnected}>
          <StatusIcon $connected={isConnected}>
            {isConnected ? <FiCheckCircle size={28} /> : <FiAlertCircle size={28} />}
          </StatusIcon>
          <StatusText>
            <StatusLabel>{isConnected ? 'Connected' : isConnecting ? 'Waiting for QR scan…' : 'Not Connected'}</StatusLabel>
            {isConnected && status?.phoneNumber && (
              <StatusDetail>Linked as <strong>{status.phoneNumber}</strong></StatusDetail>
            )}
            {!isConnected && !isConnecting && (
              <StatusDetail>Click "Connect" to generate a QR code and link your WhatsApp.</StatusDetail>
            )}
            {status?.error && (
              <StatusDetail style={{ color: colors.status?.error || '#b00020' }}>{status.error}</StatusDetail>
            )}
          </StatusText>
        </StatusCard>

        {!isConnected && qr && (
          <QRPanel>
            <QRTitle>Scan with WhatsApp on your phone</QRTitle>
            <QRImage src={qr} alt="WhatsApp QR" />
            <QRHelp>
              On your phone: WhatsApp → <strong>Settings → Linked Devices → Link a Device</strong> → point camera here.
              The QR refreshes every ~20 seconds.
            </QRHelp>
          </QRPanel>
        )}

        <Actions>
          {!isConnected && (
            <PrimaryButton onClick={handleConnect} disabled={busy || !canEdit}>
              {busy ? <FiRefreshCw className="spin" /> : <FiLink2 />}
              {busy ? 'Connecting…' : 'Connect WhatsApp'}
            </PrimaryButton>
          )}
          {(isConnected || isConnecting || status?.error || status?.qrCode || status?.phoneNumber) && (
            <DangerButton onClick={handleDisconnect} disabled={busy || !canEdit}>
              <FiLogOut /> {isConnected ? 'Disconnect' : 'Reset / Clear'}
            </DangerButton>
          )}
        </Actions>

        <InfoCard>
          <strong>How it works:</strong>
          <ul>
            <li>Session persists for ~14 days (WhatsApp's linked-device lifetime). Rescan only when the phone removes the link.</li>
            <li>Connect is one-time per company. QR usually appears within 10–30 seconds.</li>
            <li>Send invoice from any posted Sale Invoice → customer receives PDF on their WhatsApp.</li>
            <li>If status shows "Disconnected", click Connect to scan a fresh QR.</li>
          </ul>
        </InfoCard>
      </Container>
    </PageLayout>
  );
};

export default WhatsAppConnect;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Header = styled.div``;

const Title = styled.h1`
  margin: 0 0 8px;
  font-size: 24px;
  font-weight: 700;
  color: ${colors.text.primary};
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Description = styled.p`
  margin: 0;
  color: ${colors.text.secondary};
  line-height: 1.55;
`;

const Alert = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  background: ${({ $type }) =>
    $type === 'success' ? '#e8f5e9' :
    $type === 'error'   ? '#fdecea' :
                          '#e7f1ff'};
  color: ${({ $type }) =>
    $type === 'success' ? '#1b5e20' :
    $type === 'error'   ? '#b00020' :
                          '#0d47a1'};
`;

const StatusCard = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  padding: 20px;
  border-radius: 12px;
  background: ${({ $connected }) => ($connected ? '#e8f5e9' : '#fafafa')};
  border: 1px solid ${({ $connected }) => ($connected ? '#a5d6a7' : '#e0e0e0')};
`;

const StatusIcon = styled.div`
  color: ${({ $connected }) => ($connected ? '#2e7d32' : '#9e9e9e')};
`;

const StatusText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatusLabel = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${colors.text.primary};
`;

const StatusDetail = styled.div`
  font-size: 14px;
  color: ${colors.text.secondary};
`;

const QRPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid ${colors.border?.light || '#e0e0e0'};
`;

const QRTitle = styled.div`
  font-weight: 700;
  font-size: 16px;
`;

const QRImage = styled.img`
  width: 280px;
  height: 280px;
  border: 8px solid #fff;
  border-radius: 8px;
`;

const QRHelp = styled.div`
  text-align: center;
  font-size: 13px;
  color: ${colors.text.secondary};
  max-width: 360px;
  line-height: 1.5;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  background: ${colors.primary?.main || '#800020'};
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  .spin { animation: spin 1.2s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const DangerButton = styled(PrimaryButton)`
  background: #b00020;
`;

const InfoCard = styled.div`
  padding: 16px 18px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  font-size: 13px;
  color: ${colors.text.secondary};
  line-height: 1.6;
  ul { margin: 8px 0 0; padding-left: 22px; }
  li { margin-bottom: 4px; }
`;
